import React, { type FC } from 'react';
import { Form } from 'antd';
import { Modal } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';

import { putTaskClose } from 'queries';
import { CloseCallbackSteps, CloseCallbackForm, CloseCallbackActions } from '.';
import { type JsonDataItem, type WithOptionalDataTest } from 'types';
import { Toast, parseErrors, useEmailTemplates, useSendEmail, useSmsTemplates, useSendSms } from 'helpers';
import { useMutation } from 'react-query';
import styles from '../callbacks.module.scss';
import { useTaskCount } from 'hooks/useTaskCount';

export namespace CloseCallbackModal {
  export type Props = {
    record: JsonDataItem;
    isOpen: boolean;
    onOk: () => void;
    onCancel: () => void;
    step: number;
    onStepChange?: (step: number) => void;
  } & WithOptionalDataTest;

  export const $: FC<Props> = observer((props) => {
    const { onCancel, onOk, onStepChange, isOpen, record, step, dataTestId } = props;
    const [form] = Form.useForm();
    Form.useWatch([], { form });

    const commentStep = step === 0;
    const voiceMessageStep = step === 1;
    const emailStep = step === 2;
    const smsStep = step === 3;

    const { templates: emailTemplates } = useEmailTemplates(record.ID);
    const { templates: smsTemplates } = useSmsTemplates();

    const { sendEmail, isSending: emailSending } = useSendEmail(() => {
      onStepChange?.(3);
    });

    const { sendTaskSms, isSending: smsSending } = useSendSms(() => {
      closeCallback({
        taskId: record.ID,
        note: 'Unable to contact customer - closing callback',
      });
    });

    const { refresh: refreshTaskCounter } = useTaskCount();

    const { mutate: closeCallback, isLoading: closingCallback } = useMutation(putTaskClose, {
      onSuccess: (_data, payload) => {
        Toast.show({ description: 'A new note has been added.', type: Toast.Type.Success });
        Toast.show({ description: `Callback ${payload.taskId} has been closed.`, type: Toast.Type.Success });
        onOk();
        refreshTaskCounter();
      },
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    const handleCloseCallback = (data: JsonDataItem) => {
      if (commentStep) {
        closeCallback({ taskId: record.ID, note: data.callbackNote });
      }
      if (voiceMessageStep) {
        onStepChange?.(2);
      }
      if (emailStep) {
        sendEmail({ taskId: record.ID as number, ...(data as any) });
      }
      if (smsStep) {
        sendTaskSms({ taskId: record.ID, sms_to: 'C', message: data.message, to_number: data.to_number });
      }
    };

    const handleCancelModal = () => {
      onCancel();
    };

    const handleSkipModal = () => {
      emailStep && onStepChange?.(step + 1);
      smsStep && closeCallback({ taskId: record.ID, note: 'Closed with notifications' });
    };

    const handleOkModal = () => {
      form.submit();
    };

    return (
      <Modal.$
        wide
        open={isOpen}
        destroyOnClose
        maskClosable={false}
        closable={false}
        title={`Callback #${record.record_ID}`}
        dataTestId={dataTestId ?? 'close-callback-modal'}
        className={styles.callbacks__modal}
        footer={
          <CloseCallbackActions.$
            step={step}
            onOk={handleOkModal}
            onSkip={handleSkipModal}
            onCancel={handleCancelModal}
            isLoading={closingCallback || emailSending || smsSending}
            isDisabled={[2, 3].includes(step) && !form.getFieldValue('template')}
          />
        }
      >
        <CloseCallbackSteps.$ step={step} />
        <br />
        {emailTemplates && smsTemplates && (
          <Form
            form={form}
            name="close-callback-form"
            layout="horizontal"
            onFinish={handleCloseCallback}
            colon={false}
            preserve={false}
          >
            <CloseCallbackForm.$
              form={form}
              step={step}
              record={record}
              emailTemplates={emailTemplates}
              smsTemplates={smsTemplates}
            />
          </Form>
        )}
      </Modal.$>
    );
  });
}
